<template>
	<div id="watchlist-add-dialog">
		<q-stepper ref="stepper"
				   v-model="step"
				   vertical
				   color="primary"
				   animated
				   keep-alive
				   :header-nav="!disableSave"
		>
			<q-step :name="1"
					:title="$t('addToWatchlist.step.1')"
					:done="step > 1"
			>
				<div class="column">
					<div class="col" v-if="user.zohoAuthenticated">
						<customer-select v-model="selectedCustomer" :customer-name="presetCustomerName"/>
					</div>
					<div class="col">
						<watchlist-select v-model="selectedWatchlist"
										  :watchlist-name="presetWatchlistName"
										  :customer="selectedCustomer"
										  :disable="user.zohoAuthenticated && !selectedCustomer"
										  only-static
						/>
					</div>
				</div>

				<q-stepper-navigation>
					<q-btn @click="$refs.stepper.next()"
						   :label="$t('continue')"
						   color="primary"
						   :disable="disableSave"
						   no-caps
					/>
				</q-stepper-navigation>
			</q-step>

			<q-step :name="2"
					:title="$t('addToWatchlist.step.2')"
					:done="step > 2"
			>
				<div class="column">
					<div class="col">
						<q-input type="number"
								 v-model="newPrice"
								 :loading="loading"
								 :prefix="$t('label.price')"
								 :suffix="priceUnit"
								 input-class="text-right"
						/>
					</div>
					<div class="col">
						<q-input v-model="newDescription"
								 :placeholder="$t('addDescription')"
								 :disabled="loading"
								 :label="$t('label.comment')"
								 autogrow
						/>
					</div>
				</div>

				<q-stepper-navigation>
					<q-btn @click="$refs.stepper.next()"
						   :label="$t('continue')"
						   color="primary"
						   no-caps
					/>
					<q-btn @click="$refs.stepper.previous()"
						   :label="$t('previous')"
						   flat
						   no-caps
					/>
				</q-stepper-navigation>
			</q-step>

			<q-step :name="3"
					:title="$t('addToWatchlist.step.3')"
					:done="step > 3"
			>
				<q-toggle v-model="showBundleImages" :label="$t('label.showBundleImages')" :disable="disableSave"/>
				<bundle-viewer :log="objectToAdd"
							   :height="200"
							   class="q-my-sm"
				/>
				<log-images-picker :props="logImagesPickerProps"
								   v-model="excludedImages"
								   @change-teaser="changeTeaser"
								   emit
				/>

				<q-stepper-navigation>
					<q-btn @click="$refs.stepper.previous()"
						   :label="$t('previous')"
						   color="secondary"
						   no-caps
					/>
				</q-stepper-navigation>
			</q-step>
		</q-stepper>

		<div class="row q-mt-md justify-end">
			<div>
				<q-btn @click="cancel" :label="$t('button.cancel')" flat/>
			</div>
			<div>
				<q-btn @click="save" :label="$t('button.watch')" :loading="loading" :disable="disableSave"
					   color="primary"/>
			</div>
		</div>
    <div class="row q-mt-md">
      <span v-if="!checkedActiveWatchlists" class="text-primary">{{ $t('checkingActiveWatchlists') }} <q-spinner-dots color="primary"/></span>
      <q-table v-if="activeWatchlists.length" class="activeWatchlistsTable" :title="$t('activeWatchlistsTableTitle')" :data="activeWatchlists" :columns="activeWatchlistsColumns" row-key="uid" hide-bottom />
    </div>
	</div>
</template>

<script>
	import {mapGetters} from 'vuex';
	import VeneerLog from "@/components/common/VeneerLog";
  import {
    ADD_TO_WATCHLIST,
    CREATE_WATCHLIST,
    FETCH_WATCHLISTS_WITH_ITEM
  } from "@/store/actions.type";
	import WatchlistTeaser from "@/components/watchlist/WatchlistTeaser";
	import Watchlists from "@/views/Watchlists";
	import LogUtility from "@/mixins/LogUtility";
	import CustomerSelect from "@/components/zoho/CustomerSelect";
	import WatchlistSelect from "@/components/form/WatchlistSelect";
	import LogImagesPicker from "@/components/dialog/LogImagesPicker";
	import {HIDE_DIALOG} from "@/store/mutations.type";
	import BundleViewer from "@/components/detail/BundleViewer";
	import WatchlistPreselection from "@/mixins/WatchlistPreselection";
  import LogPropertiesResolver from "@/mixins/LogPropertiesResolver";
  import moment from "moment";

	export default {
		name: "WatchlistAdd",
		components: {
			BundleViewer,
			LogImagesPicker, WatchlistSelect, CustomerSelect, Watchlists, WatchlistTeaser, VeneerLog
		},
		mixins: [LogUtility, WatchlistPreselection, LogPropertiesResolver],
		props: {
			props: Object
		},
		data() {
			return {
				step: 1,
				excludedImages: [],
				loading: false,
				showBundleImages: false,
				newTeaser: '',
        checkedActiveWatchlists: false,
        activeWatchlists: [],
        activeWatchlistsColumns: [
            {name: 'name', required: true, label: this.$t('label.watchlistName'), field: 'name', align: 'left'},
            {name: 'customerName', align: 'center', label: this.$t('customer'), field: 'customerName'},
            {name: 'seller', label: this.$t('label.seller'), field: 'seller', format: (val, row) => val ? val.fullName : ''},
            {name: 'crdate', label: this.$t('label.crdate'), field: 'crdate', format: (val, row) => moment(val).format(this.$t('dateFormat.short'))},
        ]
			}
		},
		computed: {
			...mapGetters(['user', 'objectToAdd', 'watchlist', 'displayedWatchlist']),
			logImagesPickerProps() {
				return {
					item: {
						excludedImages: this.excludedImages,
						teaser: this.newTeaser,
						veneer: this.objectToAdd
					}
				}
			}
		},
    async mounted() {
      this.activeWatchlists = await this.$store.dispatch(FETCH_WATCHLISTS_WITH_ITEM, this.logId);
      this.checkedActiveWatchlists = true;
    },
		watch: {
			selectedWatchlist() {
				const matchingItem = this.containsMatchingItem();

        if(matchingItem === true) {
          this.$q.notify({
            color: 'primary',
            message: this.$t('snack.alreadyOnWatchlist')
          });
        } else if(matchingItem) {
					this.newPrice = matchingItem.price;
					this.newDescription = matchingItem.description;
					this.showBundleImages = matchingItem.showBundleImages;

					this.$q.notify({
						color: 'primary',
						message: this.$t('snack.matchingLogOnWatchlist')
					});
				} else {
					this.newPrice = 0;
					this.newDescription = '';
					this.showBundleImages = false;
				}
			}
		},
		methods: {
			cancel() {
				this.$store.commit(HIDE_DIALOG);
			},
			async save() {
				this.loading = true;
				await this.$store.dispatch(ADD_TO_WATCHLIST, {
					objects: [this.getConcreteItem(this.objectToAdd)],
					watchlist: this.selectedWatchlist.uid,
					params: {
						price: this.newPrice,
						description: this.newDescription,
						excludedImages: JSON.stringify(this.excludedImages),
						showBundleImages: this.showBundleImages,
						teaser: this.newTeaser,
						isVirtual: (this.props.isVirtual ? true : '')
					}
				});
				this.loading = false;
				this.$store.commit(HIDE_DIALOG);
			},
			createWatchlist() {
				if (this.newWatchlistName.length > 0) {
					this.$store.dispatch(CREATE_WATCHLIST, {
						name: this.newWatchlistName,
						object: this.getConcreteItem(this.objectToAdd)
					})
				}
			},
			addCurrentObjectToList(watchlist) {
				this.addObjectToWatchlist(this.objectToAdd, watchlist);
			},
			changeTeaser(imageUrl) {
				this.newTeaser = imageUrl;
			},
			containsMatchingItem() {
				if (this.selectedWatchlist) {
					const matchingItems = this.selectedWatchlist.items.filter(item => {
						let log = this.getConcreteItem(item);

						return log.logNo === this.objectToAdd.strLogNo &&
								item.speciesId === this.objectToAdd.strSpecieGid &&
								log.thicknessMm === this.objectToAdd.decThicknessMm
					});

          const identicalItem = matchingItems.some(item => {
            let log = this.getConcreteItem(item);

            return !item.virtual && this.objectToAdd.intLogId === parseInt(log.logId);
          });

          if(identicalItem) {
            return true;
          }

          return matchingItems.shift();
				}

				return false;
			}
		}
	}
</script>

<style lang="scss">
  .activeWatchlistsTable {
    max-width: 100%;

    tbody td {
      max-width: 162px;
      white-space: break-spaces;
    }
  }
</style>